import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { isInspectionReportDownloadPath, ROUTES, PLANNING_TABS } from '@/lib/constants/routes';
import { ROLES } from '@/lib/constants/app.constants';
import { hasInspectionPdfEligibleRole } from '@/lib/inspection-pdf-access';
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from '@/lib/auth/post-login-redirect';
import { ApiError } from '@/services/api/client';
import { AppError, AuthenticationError, NetworkError } from '@/lib/errors/errorTypes';
import { getB2CLoginUrl } from '@/services/auth/auth.service';

const AUTH_LOG = '[planning-auth]';

export const Login = () => {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idTokenRef = useRef<string | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  const loginUrl = useMemo(() => getB2CLoginUrl(), []);

  const hasAuthorizedRole = (userRoles: string[] | undefined): boolean => {
    if (!userRoles || userRoles.length === 0) {
      return false;
    }
    return userRoles.includes(ROLES.PLANNER) || userRoles.includes(ROLES.SYSTEM_ADMIN);
  };

  const resolveErrorMessage = useCallback((err: unknown): string => {
    if (
      err instanceof AuthenticationError ||
      err instanceof NetworkError ||
      err instanceof AppError
    ) {
      return err.message;
    }
    if (err instanceof ApiError) {
      return err.getDisplayMessage();
    }
    if (err instanceof Error) {
      return err.message || 'Failed to sign in. Please try again.';
    }
    return 'Failed to sign in. Please try again.';
  }, []);

  const handleTokenExchange = useCallback(
    async (idToken: string) => {
      // eslint-disable-next-line no-console -- login diagnostics
      console.debug(AUTH_LOG, 'Login: handleTokenExchange (B2C id_token from hash)');
      setIsLoading(true);
      setError(null);
      try {
        await signIn(idToken);
      } catch (err) {
        const message = resolveErrorMessage(err);
        // eslint-disable-next-line no-console -- login diagnostics
        console.debug(AUTH_LOG, 'Login: sign in error', message);
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [signIn, resolveErrorMessage]
  );

  useEffect(() => {
    if (isAuthenticated) {
      const fromStorage = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
      const isDownloadReturn = fromStorage ? isInspectionReportDownloadPath(fromStorage) : false;
      const canProceed = isDownloadReturn
        ? hasInspectionPdfEligibleRole(user?.roles)
        : hasAuthorizedRole(user?.roles);
      if (!canProceed) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
        navigate(ROUTES.UNAUTHORIZED);
      } else {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
        if (fromStorage) {
          navigate(fromStorage, { replace: true });
        } else {
          navigate(`${ROUTES.PLANNING}?tab=${PLANNING_TABS.LIST}`, { replace: true });
        }
      }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isAuthenticated || isProcessingRef.current) {
      return;
    }
    const init = async (): Promise<void> => {
      const hash = window.location.hash.startsWith('#')
        ? window.location.hash.substring(1)
        : window.location.hash;
      const params = new URLSearchParams(hash);
      const idToken = params.get('id_token');
      if (!idToken) {
        return;
      }
      // eslint-disable-next-line no-console -- login diagnostics
      console.debug(AUTH_LOG, 'Login: id_token in URL hash — will exchange for app JWT');
      if (idTokenRef.current !== idToken && !isProcessingRef.current) {
        idTokenRef.current = idToken;
        isProcessingRef.current = true;
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        try {
          await handleTokenExchange(idToken);
        } finally {
          isProcessingRef.current = false;
        }
      }
    };
    void init();
  }, [isAuthenticated, handleTokenExchange]);

  const handleLoginClick = (): void => {
    if (!loginUrl) {
      return;
    }
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'Login: redirecting to B2C');
    setError(null);
    window.location.href = loginUrl;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Planning App</CardTitle>
          <CardDescription>Sign in to access your planning dashboard</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">{error}</div>
          )}
          {!loginUrl && (
            <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
              Login is not configured. Please contact support.
            </div>
          )}
          <Button
            className="w-full cursor-pointer"
            onClick={handleLoginClick}
            disabled={isLoading || !loginUrl}
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            By signing in, you agree to our Terms of Service and Privacy Policy
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
